/**
 * Column-resize hook for the Master & Manager boards.
 * Persists user-set widths per (board, user, column) in localStorage.
 */
import { useCallback, useEffect, useState } from "react";

const MIN_WIDTH = 60;
const MAX_WIDTH = 900;

const storageKey = (boardKey, userId) => `board-col-widths:${boardKey}:${userId || "anon"}`;

function loadWidths(boardKey, userId) {
  try {
    const raw = window.localStorage.getItem(storageKey(boardKey, userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveWidths(boardKey, userId, widths) {
  try {
    window.localStorage.setItem(storageKey(boardKey, userId), JSON.stringify(widths));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

export function useColumnResize({ boardKey, userId } = {}) {
  const [widths, setWidths] = useState(() => loadWidths(boardKey, userId));

  useEffect(() => {
    setWidths(loadWidths(boardKey, userId));
  }, [boardKey, userId]);

  const setColumnWidth = useCallback(
    (colKey, next) => {
      setWidths((prev) => {
        const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(next)));
        const nextMap = { ...prev, [colKey]: clamped };
        saveWidths(boardKey, userId, nextMap);
        return nextMap;
      });
    },
    [boardKey, userId],
  );

  const resetColumn = useCallback(
    (colKey) => {
      setWidths((prev) => {
        const nextMap = { ...prev };
        delete nextMap[colKey];
        saveWidths(boardKey, userId, nextMap);
        return nextMap;
      });
    },
    [boardKey, userId],
  );

  const beginDrag = useCallback(
    (event, colKey, currentEl) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      const th = currentEl || event.currentTarget.closest("th");
      if (!th) return;
      const startX = event.clientX;
      const startWidth = th.getBoundingClientRect().width;
      const onMove = (moveEvent) => {
        const delta = moveEvent.clientX - startX;
        setColumnWidth(colKey, startWidth + delta);
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [setColumnWidth],
  );

  const headerStyle = useCallback(
    (colKey) => (widths[colKey] ? { width: widths[colKey], minWidth: widths[colKey] } : undefined),
    [widths],
  );

  return { widths, headerStyle, beginDrag, resetColumn };
}
